import { useRef, useState, type ChangeEvent } from 'react';
import { Button } from './Button.js';
import { Input } from './Input.js';
import { RichTextEditor } from './RichTextEditor.js';
import { Icon } from '../Icon.js';
import { IconButton } from './IconButton.js';

const MAX_ATTACH_BYTES = 25 * 1024 * 1024;

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FIELD_LABEL = {
  display: 'block',
  fontSize: 'var(--evo-fs-caption)',
  fontFamily: 'var(--evo-font)',
  color: 'var(--evo-fg-3)',
  fontWeight: 'var(--evo-fw-semibold)',
  letterSpacing: '0.5px',
  marginBottom: 'var(--evo-space-1)',
};

export interface MessageComposerProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  showSubject?: boolean;
  subject?: string;
  onSubjectChange?: (value: string) => void;
  primaryLabel: string;
  loadingLabel?: string;
  cancelLabel?: string;
  onPrimary: (attachments?: File[]) => void;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  allowAttachments?: boolean;
  attachLabel?: string;
  sizeExceededLabel?: string;
  error?: string | null;
  contextLabel?: string;
}

export function MessageComposer({
  value,
  onChange,
  placeholder,
  showSubject = false,
  subject = '',
  onSubjectChange,
  primaryLabel,
  loadingLabel,
  cancelLabel = 'Cancel',
  onPrimary,
  onCancel,
  loading = false,
  disabled = false,
  allowAttachments = false,
  attachLabel = 'Attach',
  sizeExceededLabel = 'Attachments exceed 25 MB',
  error,
  contextLabel,
}: MessageComposerProps) {
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const totalBytes = attachments.reduce((s, f) => s + f.size, 0);
  const oversize = allowAttachments && totalBytes > MAX_ATTACH_BYTES;
  const bodyEmpty = !value.replace(/<[^>]*>/g, '').trim();
  const subjectEmpty = showSubject && !subject.trim();
  const isPrimaryDisabled = disabled || bodyEmpty || subjectEmpty || oversize;

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setAttachments(prev => [...prev, ...files]);
    e.target.value = '';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {contextLabel && (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {contextLabel}
        </p>
      )}

      {showSubject && (
        <div>
          <span style={FIELD_LABEL}>SUBJECT</span>
          <Input value={subject} onChange={v => onSubjectChange?.(v)} size="sm" fullWidth />
        </div>
      )}

      <RichTextEditor
        value={value}
        onChange={onChange}
        minHeight={140}
        {...(placeholder !== undefined ? { placeholder } : {})}
      />

      {allowAttachments && attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {attachments.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
                padding: 'var(--evo-space-1) var(--evo-space-2)',
                background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r-sm)',
              }}
            >
              <span style={{ color: 'var(--evo-fg-3)', display: 'flex' }}>
                <Icon name="paperclip" size={12} />
              </span>
              <span style={{
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)',
              }}>
                {f.name}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', flexShrink: 0, fontFamily: 'var(--evo-font)' }}>
                {fmtBytes(f.size)}
              </span>
              <IconButton
                icon={<Icon name="close" size={12} />}
                ariaLabel="remove attachment"
                variant="ghost"
                tone="muted"
                size="sm"
                onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>
      )}

      {oversize && (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)' }}>
          {sizeExceededLabel}
        </p>
      )}

      {error && (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)' }}>
          {error}
        </p>
      )}

      {allowAttachments && (
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
      )}

      <div style={{ height: '1px', background: 'var(--evo-stroke)' }} />

      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          loading={loading}
          disabled={isPrimaryDisabled}
          onClick={() => onPrimary(allowAttachments ? attachments : undefined)}
        >
          {loading && loadingLabel ? loadingLabel : primaryLabel}
        </Button>
        <Button variant="outline" tone="neutral" size="sm" onClick={onCancel}>
          {cancelLabel}
        </Button>
        {allowAttachments && (
          <Button variant="outline" tone="neutral" size="sm" onClick={() => fileRef.current?.click()}>
            <Icon name="paperclip" size={11} />
            {attachLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
